import {
  IonAlert,
  IonActionSheet,
  IonButton,
  IonIcon,
  IonItem,
  IonText,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent as IonContentCmp,
  useIonToast,
  IonContent,
  IonList,
  IonAvatar,
} from "@ionic/react";
import {
  likeComment,
  reactToPost,
  recordPostShare,
  publishPostGroup,
  blockUser,
} from "../../../redux/actions";
import { Icon } from "@iconify/react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  setComment,
  setPost,
  hidePostsByUser,
} from "../../../redux/reducer/groupsSlice";
import { openCommentsSheet } from "../../../redux/reducer/commentsSheetSlice";
import { chatboxEllipses, chatbubbleOutline } from "ionicons/icons";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReportModal from "../../modals/ReportModal";
import { format } from "timeago.js";
import config from "../../../settings/config";
import { ImageLoader } from "../../ImageLoader";
import { resolveUserAvatarSrc } from "../../../services/functions";
import { useProfileNavigation } from "../../../utils/profileNavigation";
import {
  createShareEventId,
  isShareCanceledError,
  sharePost,
} from "../../../utils/postShare";
import { useAppPermission } from "../../../hooks/useAppPermission";
import PermissionPopupModal from "../../modals/PermissionPopupModal/PermissionPopupModal";
import { PERMISSION_ACTIONS } from "../../../constants/permissionActions";
import {
  canModeratePost,
  isPostPublished,
} from "../../../utils/postStatus";
import { PERMISSION_LEVEL } from "../../../redux/reducer/permissionsSlice";
import { MAX_COMMENT_LENGTH } from "../../../constants/commentLimits";
import { useKeyboardHeight } from "../../../hooks/useKeyboardHeight";
import "./PostFooter.css";
import {
  arePostCommentsEnabled,
  arePostReactionsEnabled,
  isPostDatetimeHidden,
} from "../../../utils/postComments";
import {
  COMMENT_REACTIONS,
  REACTION_LIKE,
  getCurrentPostReaction,
  getLikeItemReactionType,
  getReactionEmoji,
  getReactionLabel,
  topReactionTypes,
} from "../../../services/commentReactions";
import { getAuthToken } from "../../../services/authStorage";

// Lowest viewport y the reaction picker's bottom edge may take. The picker is
// translated -100% vertically, so this keeps it fully on screen (its own height
// plus a margin) when the anchoring button sits near the top of the sheet.
const MIN_PICKER_TOP = 88;

export default function PostFooter({
  match,
  postGroup = null,
  comment = null,
  is_reply,
  from_main_group = true,
  is_preview = false,
  only_post = false,
  classes = "",
  comments = true,
  likes = true,
  reports = true,
  date = true,
  is_multimedia = false,
  onDelete,
  onEdit,
  // Bottom-sheet drill-in: when provided, the reply button on a comment opens
  // its thread inside the sheet instead of navigating to a full page.
  onCommentReply,
  layout = "detail", // 'list' or 'detail'
  postActionsRef = null,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { goToProfile, canOpenProfile } = useProfileNavigation();
  const [present] = useIonToast();
  const [reportIsOpen, setReportIsOpen] = useState(false);
  const [deniedReasonIsOpen, setDeniedReasonIsOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [commentMenuOpen, setCommentMenuOpen] = useState(false);
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // The Salva button kept ending up under the iPhone keyboard. The height has to
  // be measured natively: on iOS the layout viewport keeps its full height while
  // the keyboard is up, so `100vh` and visualViewport both lie (see
  // useKeyboardHeight).
  const { keyboardHeight: editKeyboardHeight, viewportHeight: editViewportHeight } =
    useKeyboardHeight(editOpen);

  // Published on :root rather than through IonAlert's `style` prop: the Ionic
  // React wrapper does not forward arbitrary DOM props to the <ion-alert> host,
  // so the custom properties never reached it and the rules that depend on them
  // were dead. The alert renders in a portal at the end of <body>, so :root is
  // the one place it is certain to inherit from. PostFooter.css explains what
  // the two values do.
  useEffect(() => {
    const root = document.documentElement;

    if (!editOpen) {
      root.style.removeProperty("--ds-edit-kb");
      root.style.removeProperty("--ds-edit-vh");
      return undefined;
    }

    root.style.setProperty("--ds-edit-kb", `${editKeyboardHeight}px`);
    if (editViewportHeight) {
      root.style.setProperty("--ds-edit-vh", `${editViewportHeight}px`);
    }

    return () => {
      root.style.removeProperty("--ds-edit-kb");
      root.style.removeProperty("--ds-edit-vh");
    };
  }, [editOpen, editKeyboardHeight, editViewportHeight]);
  const [likesListOpen, setLikesListOpen] = useState(false);
  const [likesList, setLikesList] = useState([]);
  // Reactions a recurring post carried over from earlier occurrences: counted
  // in the total but not listed by name (client, 25/09/2026).
  const [likesInheritedCount, setLikesInheritedCount] = useState(0);

  const {
    checkPermission: checkLikePermission,
    isModalOpen: isLikeModalOpen,
    setIsModalOpen: setIsLikeModalOpen,
    popupText: likePopupText,
    isBlocked: isLikeBlocked,
  } = useAppPermission(PERMISSION_ACTIONS.FEED_COMMUNITY_LIKE);

  const {
    level: viewCommentsLevel,
    isLoaded: isViewCommentsLoaded,
    checkPermission: checkViewCommentsPermission,
    isModalOpen: isViewCommentsModalOpen,
    setIsModalOpen: setIsViewCommentsModalOpen,
    popupText: viewCommentsPopupText,
  } = useAppPermission(PERMISSION_ACTIONS.FEED_COMMUNITY_VIEW_COMMENTS);

  const profile = useSelector((state) => state.user);
  const user = profile.user;
  const userId = user.id;
  const telephone = user.telephone;
  const currentUserBadge = profile?.master_club_subscriber?.badge ?? null;
  const currentUserSubscriber = profile?.master_club_subscriber;

  const element = postGroup || comment;
  const element_id = element.id;
  const commentCountFallback = element.comments_count;

  const is_enabled = isPostPublished(postGroup);
  const post_id = postGroup ? element.post.id : null;
  const post_group_id = postGroup ? element.id : null;

  const replies_count = element.replies_count;

  // Post timeago always uses publish_at (e.g. "2026-07-01 23:29:36").
  // Comments fall back to created_at / updated_at. None at all when the post
  // has "Nascondi data e ora del post" on.
  const rawTimeAgoDate = postGroup
    ? isPostDatetimeHidden(postGroup)
      ? null
      : element.post?.publish_at
    : element?.created_at ?? element?.updated_at;
  const parsedTimeAgoDate = rawTimeAgoDate
    ? new Date(String(rawTimeAgoDate).replace(" ", "T"))
    : null;
  const createdBy = postGroup
    ? element.post.created_by_user_id
    : element.created_by_user_id;
  const timeAgo =
    parsedTimeAgoDate && !Number.isNaN(parsedTimeAgoDate.getTime())
      ? format(parsedTimeAgoDate, "itLocale")
      : "";
  const [reported, setReported] = useState(element.reported);
  const [liked, setLiked] = useState(element.liked);
  // A comment's tally covers its whole thread: its own reactions plus those on
  // its replies (`likes_count_with_replies`). The like BUTTON still acts on the
  // comment alone — `liked` / `my_reaction` below are unchanged — so an
  // optimistic ±1 stays correct either way. Falls back to the plain count for
  // posts, and for a backend that predates the field.
  const [likesCount, setLikesCount] = useState(
    element.likes_count_with_replies ?? element.likes_count ?? 0,
  );
  const lastLikeName = element.last_like;
  // Which reaction THIS user picked (null = none), plus the long-press picker.
  // Post reactions are mirrored through Redux; comments keep this local state.
  const [myReaction, setMyReaction] = useState(element.my_reaction || null);
  // Per-type tallies for THIS comment, kept in step with the user's own
  // reactions. The summary under a comment reads these; it used to read
  // `element.reactions` straight from the payload, which nothing ever updated —
  // so once the server had said {love: 1}, switching to 👍 still displayed ❤️
  // for the rest of the session.
  const [commentReactions, setCommentReactions] = useState(() => ({
    ...(element?.reactions_with_replies || element?.reactions || {}),
  }));

  // removed/added are reaction types (either may be null).
  const applyReactionDelta = (removed, added) => {
    setCommentReactions((prev) => {
      const next = { ...prev };
      if (removed) {
        next[removed] = Math.max(0, (Number(next[removed]) || 0) - 1);
      }
      if (added) {
        next[added] = (Number(next[added]) || 0) + 1;
      }
      return next;
    });
  };
  const [postReactionOverride, setPostReactionOverride] = useState(undefined);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  // Viewport y of the button the picker was opened from (see startLongPress).
  const [pickerAnchorTop, setPickerAnchorTop] = useState(null);
  const pickerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  const postStateFromStore = useSelector((state) => {
    if (!postGroup?.id) return null;

    const id = String(postGroup.id);
    const groupSelected = from_main_group ? "main" : "not_main";
    const segmentSelected = postGroup?.post?.is_fixed ? "fixed" : "general";
    const posts =
      state.groups.getGroupPosts[groupSelected]?.[segmentSelected]?.data?.posts;
    const postInFeed = Array.isArray(posts)
      ? posts.find((p) => String(p?.id) === id)
      : null;
    const pg = state.groups.getPost?.data?.post_group;
    const getPostMatches = pg && String(pg.id) === id;

    if (layout === "list") {
      if (postInFeed) return postInFeed;
      if (getPostMatches) return pg;
      return postGroup;
    }

    if (getPostMatches) return pg;
    if (postInFeed) return postInFeed;
    return postGroup;
  });
  const likedFromStore = !!(postStateFromStore ?? postGroup)?.liked;
  const likesCountFromStore =
    (postStateFromStore ?? postGroup)?.likes_count ?? 0;
  const reactionsFromStore =
    (postStateFromStore ?? postGroup)?.reactions ?? element?.reactions ?? {};
  // Older feed payloads expose `liked` and the aggregate map but omit the
  // current user's type. Infer it exactly when the aggregate has one type.
  const myReactionFromStore = getCurrentPostReaction(
    postStateFromStore ?? postGroup,
  );
  const activeReaction = postGroup
    ? postReactionOverride !== undefined
      ? postReactionOverride
      : myReactionFromStore
    : myReaction;

  useEffect(() => {
    setPostReactionOverride(undefined);
  }, [postGroup?.id]);

  // Read the comment count from the store the same way as likes, so posting a
  // comment (which bumps comments_count in getGroupPosts/getPost) updates the
  // feed badge live instead of only after a refetch. Falls back to the prop.
  const commentsCountFromStore = useSelector((state) => {
    const fallback = commentCountFallback ?? postGroup?.comments_count ?? 0;
    if (!postGroup?.id) return fallback;

    const id = String(postGroup.id);
    const groupSelected = from_main_group ? "main" : "not_main";
    const segmentSelected = postGroup?.post?.is_fixed ? "fixed" : "general";
    const posts =
      state.groups.getGroupPosts[groupSelected]?.[segmentSelected]?.data?.posts;
    const postInFeed = Array.isArray(posts)
      ? posts.find((p) => String(p?.id) === id)
      : null;
    const pg = state.groups.getPost?.data?.post_group;
    const getPostMatches = pg && String(pg.id) === id;

    if (layout === "list") {
      if (postInFeed) return postInFeed.comments_count ?? fallback;
      if (getPostMatches) return pg.comments_count ?? fallback;
      return fallback;
    }
    if (getPostMatches) return pg.comments_count ?? fallback;
    if (postInFeed) return postInFeed.comments_count ?? fallback;
    return fallback;
  });
  const comment_count = commentsCountFromStore;
  // Store-first so the detail page and comments sheet react to a toggle too.
  // The `!postGroup ||` prefix matters: this component also renders comment
  // footers, where there is no postGroup and both must stay enabled.
  const postSource = postStateFromStore ?? postGroup;
  const postCommentsEnabled = !postGroup || arePostCommentsEnabled(postSource);
  const postReactionsEnabled = !postGroup || arePostReactionsEnabled(postSource);
  // Both off: the card drops its counters and its action row, keeping only the
  // "..." trigger (the sole entry point to report/block and the badge menu).
  const bothInteractionsDisabled =
    !!postGroup && !postCommentsEnabled && !postReactionsEnabled;
  const sharesCountFromStore =
    (postStateFromStore ?? postGroup)?.shares_count ?? 0;
  const [shareCountOverride, setShareCountOverride] = useState(undefined);
  const shareCount =
    shareCountOverride !== undefined
      ? shareCountOverride
      : sharesCountFromStore;

  useEffect(() => {
    setShareCountOverride(undefined);
  }, [postGroup?.id]);

  useEffect(() => {
    if (postGroup) {
      setLiked(likedFromStore);
      setLikesCount(likesCountFromStore);
    } else {
      // Comments/replies don't live in the feed store, so their like state is
      // seeded from the fetched object at mount only. Reconcile it whenever the
      // fetched like data changes, otherwise a footer mounted with stale data
      // (comment showed 0 likes, then reopened from a "X ha messo mi piace al
      // tuo commento" notification) keeps the mount-time count and never shows
      // the new like.
      setLiked(element.liked);
      setLikesCount(element.likes_count_with_replies ?? element.likes_count ?? 0);
      setMyReaction(element.my_reaction || null);
    }
  }, [
    postGroup?.id,
    likedFromStore,
    likesCountFromStore,
    element.liked,
    element.likes_count,
    element.likes_count_with_replies,
    element.my_reaction,
  ]);

  const commentId = match?.params?.commentId;

  const commentHandleClick = () => {
    if (isViewCommentsLoaded && viewCommentsLevel === PERMISSION_LEVEL.POPUP) {
      checkViewCommentsPermission();
      return;
    }

    if (postGroup) {
      if (!is_preview) return;
      if (!postCommentsEnabled) return;
      // Open the comments bottom sheet instead of navigating to a full page.
      // Keep setPost so the likes selectors that read getPost still resolve.
      // No focusPostId write: the feed stays mounted, nothing to scroll back to.
      dispatch(setPost({ post_group: { ...postGroup, from_main_group } }));
      dispatch(
        openCommentsSheet({
          postGroup: { ...postGroup, from_main_group },
          from_main_group,
        })
      );
      // Scroll the feed so the tapped post stays visible above the sheet, so
      // it's clear which post's comments are open (two posts can be on screen).
      const focusedPostId = element_id;
      setTimeout(() => {
        document
          .getElementById(`post-${focusedPostId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
      return;
    }
    if (comment) {
      // Inside the sheet, the reply button drills into the thread in place.
      if (onCommentReply) {
        onCommentReply();
        return;
      }
      dispatch(setComment(comment));
      history.push(window.location.pathname + "/" + element_id, "forward");
    }
  };

  const likeResponse = (res) => {
    if (!res) return;
    if (comment) {
      setLiked(!liked);
      setLikesCount(liked ? likesCount - 1 : likesCount + 1);
    }
  };

  // ── Comment reactions [21] ─────────────────────────────────────────────────
  // Long-press the comment's "Mi piace" opens the picker; a plain tap keeps the
  // old behaviour. Comments only — the postGroup path is untouched.
  const cancelLongPress = () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  };

  // Place the picker once it exists.
  //
  // `position: fixed` is NOT reliably viewport-relative here: inside the
  // comments sheet the nearest ancestor with a transform is Ionic's
  // `.ion-delegate-host.ion-page`, which becomes the containing block. A top of
  // 532 (correct in viewport terms) then rendered at y=857 on an 812-tall
  // screen — the bar opened below the fold. So the button's viewport y has to be
  // rebased onto that containing block.
  //
  // This used to ask `el.offsetParent` where the block starts, which is wrong on
  // iOS. CSSOM specifies offsetParent as null when the element's own position is
  // fixed, and the engines disagree about honouring it — measured directly:
  //
  //     Blink  (Chrome 148)          -> DIV#host   (the transformed ancestor)
  //     WebKit (iOS 18.6/Safari 26)  -> NULL
  //
  // On iPhone `parentTop` was therefore always 0, the rebase silently became a
  // no-op, and the bar opened far outside the sheet. That is the whole of the
  // "reaction emojis have disappeared on iPhone" report — they were rendering,
  // just nowhere visible.
  //
  // Probing sidesteps the disagreement entirely: pin `top` to 0, read back where
  // the element actually landed, and that IS the containing block's origin in
  // viewport coordinates — on every engine, transformed ancestor or not.
  useLayoutEffect(() => {
    const el = pickerRef.current;
    if (!reactionPickerOpen || !el || pickerAnchorTop === null) return;

    el.style.top = "0px";
    const probe = el.getBoundingClientRect();
    // translate(-100%) hangs the box one full height above `top`, so the
    // origin is the measured top plus that height.
    const originTop = probe.top + probe.height;

    // The -100% translate makes `top` the bar's BOTTOM edge, so this puts it
    // 8px above the button; floored so it never opens off the top of the sheet.
    const desired = Math.max(pickerAnchorTop - 8, MIN_PICKER_TOP + originTop);
    el.style.top = `${desired - originTop}px`;
  }, [reactionPickerOpen, pickerAnchorTop]);

  const startLongPress = (event) => {
    if (!comment && !postGroup) return;
    // Where the picker should sit vertically. Captured on press, because the
    // picker is centred in the VIEWPORT (position: fixed) rather than anchored
    // to this button — see .reaction-picker. Anchoring it to the button meant
    // opening leftward from wherever the thumb happened to be, and on a comment
    // row the thumb sits mid-column, so the first emoji fell off the left edge.
    const rect = event?.currentTarget?.getBoundingClientRect?.();
    longPressFiredRef.current = false;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      if (rect) setPickerAnchorTop(rect.top);
      setReactionPickerOpen(true);
    }, 450);
  };

  // Apply a reaction (love/hug/haha/sad/wow or plain like) to a POST. Same
  // toggle rules as comments: tapping the current reaction removes it, picking a
  // different one swaps it (count unchanged). The store update is reaction-aware
  // (reactToPost), and my_reaction is tracked locally (the feed store doesn't
  // re-seed it for posts).
  const reactPostToggle = (reactionType) => {
    if (!postGroup) return;
    if (!postReactionsEnabled) return;
    const prevReaction = activeReaction || null;
    const prevLiked = likedFromStore;
    const isSame = prevReaction === reactionType;
    const willBeLiked = !isSame;
    const delta = isSame ? -1 : prevReaction ? 0 : 1;
    setPostReactionOverride(willBeLiked ? reactionType : null);
    dispatch(
      reactToPost({
        element_id,
        is_fixed: postGroup.post.is_fixed,
        from_main_group,
        only_post,
        reaction_type: reactionType,
        delta,
        willBeLiked,
        prevReaction,
        prevLiked,
        prevLikesCount: likesCountFromStore,
        prevReactions: { ...reactionsFromStore },
      }),
    ).then(() => setPostReactionOverride(undefined));
  };

  const handleReactionPick = (reactionType) => {
    setReactionPickerOpen(false);
    if (postGroup) {
      reactPostToggle(reactionType);
      return;
    }
    if (!comment) return;
    const isSame = myReaction === reactionType;
    dispatch(
      likeComment({
        element_id: comment.id,
        is_reply,
        reaction_type: reactionType,
      }),
    ).then((res) => {
      if (!res) return;
      if (isSame) {
        // Same reaction again → the backend removes it.
        setMyReaction(null);
        setLiked(false);
        setLikesCount((c) => Math.max(0, c - 1));
        applyReactionDelta(reactionType, null);
      } else {
        // Swapping keeps the total; only a brand-new reaction increments it.
        const hadReaction = !!myReaction;
        setMyReaction(reactionType);
        applyReactionDelta(myReaction || null, reactionType);
        if (!hadReaction) {
          setLiked(true);
          setLikesCount((c) => c + 1);
        }
      }
    });
  };

  const likeHandleClick = () => {
    // Swallow the click that ends a long-press — the picker is already open.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (postGroup) {
      if (!postReactionsEnabled) return;
      if (isLikeBlocked) return;
      const { allowed } = checkLikePermission();
      if (!allowed) return;
      console.log("Checking like permission...", isLikeBlocked);
    }

    if (postGroup) {
      // Tap toggles the post's reaction: an existing reaction is removed, no
      // reaction adds a plain like. Same rule as comments, via reactToPost so
      // the reaction-aware store update stays correct.
      reactPostToggle(activeReaction || REACTION_LIKE);
    }

    if (comment) {
      // Tapping toggles: when the user already has ANY reaction, send that same
      // one back so the backend removes it; otherwise send the default like.
      dispatch(
        likeComment({
          element_id: comment.id,
          is_reply,
          reaction_type: myReaction || REACTION_LIKE,
        }),
      ).then((res) => {
        likeResponse(res);
        const had = myReaction;
        setMyReaction(had ? null : REACTION_LIKE);
        applyReactionDelta(had || null, had ? null : REACTION_LIKE);
      });
    }
  };

  // Shared bits so both footer layouts (list + detail) render reactions the
  // same way — for both comments and posts (long-press opens the picker).
  const canReact = !!comment || !!postGroup;
  // No picker on a post whose reactions are off, even from a stale render.
  const likeLongPressProps = canReact && postReactionsEnabled
    ? {
        onTouchStart: startLongPress,
        onTouchEnd: cancelLongPress,
        onTouchMove: cancelLongPress,
        onMouseDown: startLongPress,
        onMouseUp: cancelLongPress,
        onMouseLeave: cancelLongPress,
        onContextMenu: (e) => e.preventDefault(),
      }
    : {};

  const likeIconNode =
    canReact && activeReaction && activeReaction !== REACTION_LIKE ? (
      <span className="reaction-emoji">{getReactionEmoji(activeReaction)}</span>
    ) : (
      <Icon icon={is_multimedia ? "bxs:star" : "mdi:like"} width="24" />
    );

  const reactionPickerNode =
    canReact && reactionPickerOpen ? (
      <>
        <div
          className="reaction-picker__backdrop"
          onClick={(e) => {
            e.stopPropagation();
            setReactionPickerOpen(false);
          }}
        />
        <div
          ref={pickerRef}
          className="reaction-picker"
          onClick={(e) => e.stopPropagation()}
        >
          {COMMENT_REACTIONS.map((r) => (
            <button
              type="button"
              key={r.type}
              className={
                "reaction-picker__item" +
                (activeReaction === r.type ? " is-active" : "")
              }
              aria-label={r.label}
              onClick={(e) => {
                e.stopPropagation();
                handleReactionPick(r.type);
              }}
            >
              {r.emoji}
            </button>
          ))}
        </div>
      </>
    ) : null;
  const handlePostStatus = (status_id, denied_reason = null) => {
    dispatch(
      publishPostGroup({
        post_group_id,
        status_id,
        denied_reason,
      }),
    ).then((res) => {
      if (res.payload.status === 200) {
        return present({
          message: status_id === 2 ? "Post pubblicato" : "Post rifiutato",
          duration: 3000,
        }).then(() => {
          // location.reload()
        });
      }
    });
  };

  const handleDeniedReason = (e) => {
    const denied_reason = e.message;
    if (denied_reason) {
      handlePostStatus(3, denied_reason);
    }
  };

  const openLikesList = async () => {
    if (!likesCount) return;
    try {
      const token = getAuthToken();
      const element_type = comment
        ? "App\\Models\\MasterClubComment"
        : "App\\Models\\MasterClubPostGroup";
      const url = new URL(
        `${config.env[config.env.mode].url}/api/master-club-likes/list`,
      );
      url.searchParams.set("element_type", element_type);
      url.searchParams.set("element_id", String(element_id));
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("likes list fetch error");
      const data = await res.json();
      const users =
        (Array.isArray(data?.users) && data.users) ||
        (Array.isArray(data?.data?.users) && data.data.users) ||
        [];
      setLikesList(users);
      setLikesInheritedCount(Number(data?.inherited_count ?? data?.data?.inherited_count) || 0);
      setLikesListOpen(true);
    } catch (e) {
      present({
        message: "Errore nel caricamento dei like",
        duration: 2500,
        color: "danger",
      });
    }
  };

  const canDelete =
    !!comment && (userId === comment?.created_by?.id || !!currentUserBadge);

  // Only the comment's author can edit its text. Authors get a three-dots menu
  // (Modifica / Elimina); moderators keep delete-only.
  const isCommentAuthor = !!comment && userId === comment?.created_by?.id;

  const editComment = async (newContent) => {
    const trimmed = String(newContent ?? "").trim();
    if (!trimmed) return;
    try {
      const token = getAuthToken();
      const res = await fetch(
        `${config.env[config.env.mode].url}/api/v1/master-club-comments/${
          comment.id
        }`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ content: trimmed }),
        },
      );
      if (res.ok) {
        onEdit?.(trimmed);
        present({ message: "Commento aggiornato", duration: 2000 });
      } else {
        present({
          message: "Errore nella modifica",
          duration: 2500,
          color: "danger",
        });
      }
    } catch (e) {
      present({
        message: "Errore nella modifica",
        duration: 2500,
        color: "danger",
      });
    }
  };

  const deleteComment = async () => {
    try {
      // optimistic hide
      onDelete?.();
      const token = getAuthToken();
      const res = await fetch(
        `${config.env[config.env.mode].url}/api/master-club-comments/${
          comment.id
        }`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.status === 200) {
        present({ message: "Eliminato con successo", duration: 2500 });
      } else {
        present({
          message: "Errore nell'eliminazione",
          duration: 2500,
          color: "danger",
        });
      }
    } catch (e) {
      present({
        message: "Errore nell'eliminazione",
        duration: 2500,
        color: "danger",
      });
    }
  };

  const handleShareOpen = async () => {
    if (!postGroup) return;
    try {
      const result = await sharePost({ postGroup });
      if (result?.shared || result?.copied) {
        try {
          const engagement = await dispatch(
            recordPostShare({
              element_id: postGroup.id,
              event_id: createShareEventId(),
            }),
          ).unwrap();
          setShareCountOverride(Number(engagement?.shares_count) || 0);
        } catch (trackingError) {
          console.error("Error recording post share:", trackingError);
          present({
            message:
              "Condivisione completata, ma il conteggio non è stato aggiornato.",
            duration: 2500,
            color: "warning",
          });
        }
      }
      if (result?.copied) {
        present({
          message: "Link del post copiato",
          duration: 1800,
          position: "top",
        });
      }
    } catch (error) {
      if (isShareCanceledError(error)) return;
      console.error("Error sharing post:", error);
      present({
        message: "Impossibile condividere il post. Riprova.",
        duration: 2500,
        color: "danger",
      });
    }
  };

  const isCommentDisabled =
    !!(postGroup && !is_preview) || !!(comment && commentId);
  const showComment =
    (comments && postCommentsEnabled && postGroup && is_preview) ||
    // Top-level comments always show the reply action; replies show it only when
    // a reply handler is provided (the flat comments sheet — reply to a reply).
    (comment && (!is_reply || !!onCommentReply));
  return (
    <>
      {postGroup && canModeratePost(postGroup) && currentUserSubscriber?.badge && (
        <>
          <IonButton
            className="force-enabled ion-margin-horizontal"
            onClick={() => handlePostStatus(2)}
          >
            Pubblicare
          </IonButton>
          <IonButton
            className="force-enabled ion-margin-horizontal"
            color="danger"
            onClick={() => setDeniedReasonIsOpen(true)}
          >
            Rifiutare
          </IonButton>
          <IonAlert
            header="Motivo del rifiuto"
            className="report-problem-alert"
            isOpen={deniedReasonIsOpen}
            buttons={[
              {
                text: "Annulla",
                role: "cancel",
              },
              {
                text: "Invia",
                role: "confirm",
                handler: (e) => {
                  handleDeniedReason(e);
                },
              },
            ]}
            inputs={[
              {
                name: "message",
                id: "denied-post-reason",
                type: "textarea",
                placeholder: "Scrivi un messaggio per l’utente",
              },
            ]}
          ></IonAlert>
        </>
      )}
      {layout === "list" && postGroup ? (
        <div
          className={
            "post-reactions-summary " +
            (bothInteractionsDisabled
              ? "post-reactions-summary--menu-only "
              : "") +
            classes
          }
        >
          {postReactionsEnabled && (
            <button
              type="button"
              className="reactions-summary__reacts"
              onClick={likesCount > 0 ? openLikesList : undefined}
              disabled={likesCount <= 0}
              aria-label={`${likesCount} reazioni`}
            >
              <span className="reactions-summary__icons">
                {(topReactionTypes(reactionsFromStore).length
                  ? topReactionTypes(reactionsFromStore)
                  : [activeReaction || REACTION_LIKE]
                ).map((type) => (
                  <span key={type} className="reaction-emoji">
                    {getReactionEmoji(type)}
                  </span>
                ))}
              </span>
              {likesCount > 0 && (
                <span className="reactions-summary__count">{likesCount}</span>
              )}
            </button>
          )}

          {!bothInteractionsDisabled && (
            <button
              type="button"
              className="reactions-summary__comments"
              onClick={
                postCommentsEnabled ? () => commentHandleClick() : undefined
              }
              disabled={!postCommentsEnabled}
              aria-label={`${comment_count ?? 0} commenti`}
            >
              <IonIcon
                icon={chatbubbleOutline}
                className="reactions-summary__comments-icon"
              />
              {(comment_count ?? 0) > 0 && <span>{comment_count}</span>}
            </button>
          )}

          {!bothInteractionsDisabled && is_enabled && (
            <div
              className="reactions-summary__shares"
              aria-label={`${shareCount} condivisioni`}
            >
              <Icon
                icon="mdi:share-outline"
                width="24"
                height="24"
                aria-hidden="true"
              />
              {shareCount > 0 && <span>{shareCount}</span>}
            </div>
          )}

          <button
            type="button"
            className="post-actions-menu-trigger"
            aria-label="Azioni post"
            onClick={(event) => {
              event.stopPropagation();
              postActionsRef?.current?.open?.(event);
            }}
          >
            <Icon icon="mdi:dots-vertical" width="28" height="28" />
          </button>
        </div>
      ) : (
        ((postReactionsEnabled && likesCount > 0) ||
          (postCommentsEnabled && (comment_count ?? replies_count) > 0)) && (
        <IonItem
          lines="none"
          className={"post-footer post-reactions-summary " + classes}
        >
          {/* LEFT: the reaction types actually used + the total count. */}
          {likes && postReactionsEnabled && likesCount > 0 && (
            <div
              className="reactions-summary__reacts"
              slot="start"
              onClick={openLikesList}
            >
              <span className="reactions-summary__icons">
                {(topReactionTypes(
                  postGroup ? reactionsFromStore : commentReactions,
                ).length
                  ? topReactionTypes(
                      postGroup ? reactionsFromStore : commentReactions,
                    )
                  : [activeReaction || REACTION_LIKE]
                ).map((t) => (
                  <span key={t} className="reaction-emoji">
                    {getReactionEmoji(t)}
                  </span>
                ))}
              </span>
              <span className="reactions-summary__count">{likesCount}</span>
            </div>
          )}
          {/* RIGHT: comment count with a small bubble icon (no "Commenti"
              label). slot="end" keeps it right-aligned like Facebook, so a
              comments-only post never reads as a stray number under the text. */}
          {postCommentsEnabled && (comment_count ?? replies_count) > 0 && (
            <div
              className="reactions-summary__comments"
              slot="end"
              onClick={() => commentHandleClick()}
            >
              <IonIcon
                icon={chatbubbleOutline}
                className="reactions-summary__comments-icon"
              />
              <span>{comment_count ?? replies_count}</span>
            </div>
          )}
        </IonItem>
        )
      )}

      {/* Layout for post lists */}
      {layout === "list" ? (
        // Both off: no counters, no actions — only the "..." row above.
        bothInteractionsDisabled ? null : (
        <div className={"post-footer-list " + classes}>
          <div className="post-footer-list-container">
            {reactionPickerNode}
            {likes && (
              <button
                type="button"
                className={
                  "like-section liked-" +
                  (postGroup
                    ? likedFromStore
                      ? "true"
                      : "false"
                    : liked
                    ? "true"
                    : "false") +
                  (postGroup && isLikeBlocked ? " permission-blocked" : "") +
                  (!postReactionsEnabled ? " disable-click" : "")
                }
                onClick={postReactionsEnabled ? likeHandleClick : undefined}
                disabled={!postReactionsEnabled}
                {...(postReactionsEnabled ? likeLongPressProps : {})}
              >
                <div className="section-content">
                  <div className="like-icon-container">{likeIconNode}</div>
                  <IonText color="gray" className="section-label">
                    {getReactionLabel(activeReaction || REACTION_LIKE)}
                  </IonText>
                </div>
              </button>
            )}
            {(showComment || (postGroup && is_preview)) && (
              <button
                type="button"
                className={
                  "comment-section" +
                  (!postCommentsEnabled ? " disable-click" : "")
                }
                onClick={
                  postCommentsEnabled ? () => commentHandleClick() : undefined
                }
                disabled={!postCommentsEnabled}
              >
                <div className="section-content">
                  <div className="comment-icon-container">
                    <IonIcon src={chatboxEllipses} color="light-gray" />
                  </div>
                  <IonText color="gray" className="section-label">
                    Commenta
                  </IonText>
                </div>
              </button>
            )}
            {is_enabled && postGroup && (
              <button
                type="button"
                className="share-section"
                onClick={handleShareOpen}
              >
                <div className="section-content">
                  <div className="share-icon-container">
                    <Icon
                      icon="mdi:share-outline"
                      width="26"
                      height="26"
                    />
                  </div>
                  <IonText color="gray" className="section-label">
                    Condividi
                  </IonText>
                </div>
              </button>
            )}
          </div>
        </div>
        )
      ) : (
        /* Layout for detail pages (comments, individual posts) */
        <IonItem lines="none" className={"post-footer " + classes}>
          <div className="post-time-ago" slot="start">
            {date && timeAgo && <IonText color="gray">{timeAgo}</IonText>}
          </div>
          <div slot="end" className="right-buttons">
            {showComment &&
              (comment && onCommentReply ? (
                /* Comment context: an explicit, unambiguous reply action.
                   (The bubble icon read as a comment-count badge.)
                   Shown on replies too — replying to a reply is flattened to the
                   same parent and records "In risposta a <name>" (reply-to-reply). */
                <button
                  type="button"
                  className={
                    "comment-reply-text" +
                    (isCommentDisabled ? " disable-click" : "")
                  }
                  onClick={() => commentHandleClick()}
                >
                  Rispondi
                </button>
              ) : (
                <div
                  className={
                    "comment-button" +
                    (isCommentDisabled ? " disable-click" : "")
                  }
                  onClick={() => commentHandleClick()}
                >
                  <div className="comment-icon-container">
                    <IonIcon src={chatboxEllipses} color="light-gray" />
                    {(comment_count ?? replies_count) > 0 && (
                      <div className="comment-badge">
                        {comment_count ?? replies_count}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            {likes && postReactionsEnabled && (
              <div
                className={
                  "like-button liked-" +
                  (postGroup
                    ? likedFromStore
                      ? "true"
                      : "false"
                    : liked
                    ? "true"
                    : "false")
                }
              >
                <div
                  className="like-icon-container"
                  style={{ position: "relative" }}
                  onClick={likeHandleClick}
                  {...likeLongPressProps}
                >
                  {reactionPickerNode}
                  {/* Comments get a plain thumb whose fill mirrors whether this
                      user has reacted — tap toggles it, long-press opens the
                      picker. Deliberately NOT the chosen emoji, and no count:
                      which reactions were used and how many is already shown in
                      the summary directly beneath the comment, and repeating it
                      here made the control read as a counter rather than a
                      button. Posts keep the emoji + badge. */}
                  {comment ? (
                    <Icon
                      icon={activeReaction ? "mdi:like" : "mdi:like-outline"}
                      width="24"
                    />
                  ) : (
                    likeIconNode
                  )}
                  {!comment && likesCount > 0 && (
                    <div className="like-badge">{likesCount}</div>
                  )}
                </div>
              </div>
            )}
            {is_enabled && postGroup && (
              <div className="share-button" onClick={handleShareOpen}>
                <Icon icon="mdi:arrow-top-right-thick" width="24" />
              </div>
            )}

            {/* Single "⋯" overflow grouping every available action for this
                comment — Modifica (author), Elimina (author/moderator), Segnala
                (others). Avoids a standalone trash icon alongside the menu. */}
            {(() => {
              const canReport =
                reports && userId !== element?.created_by?.id && !reported;
              const menuButtons = [];
              if (isCommentAuthor) {
                menuButtons.push({
                  text: "Modifica",
                  handler: () => setEditOpen(true),
                });
              }
              if (canDelete) {
                menuButtons.push({
                  text: "Elimina",
                  role: "destructive",
                  handler: () => setConfirmDeleteOpen(true),
                });
              }
              if (canReport) {
                menuButtons.push({
                  text: "Segnala",
                  role: "destructive",
                  handler: () => setReportIsOpen(true),
                });
              }
              const authorId = element?.created_by?.id;
              if (authorId && userId !== authorId) {
                menuButtons.push({
                  text: "Blocca utente",
                  role: "destructive",
                  handler: () => setBlockConfirmOpen(true),
                });
              }
              if (!menuButtons.length) return null;
              menuButtons.push({ text: "Annulla", role: "cancel" });

              return (
                <>
                  <div
                    className={"comment-menu-button reported-" + reported}
                    onClick={() => setCommentMenuOpen(true)}
                  >
                    <Icon icon="mdi:dots-horizontal" width="22" />
                  </div>
                  <IonActionSheet
                    isOpen={commentMenuOpen}
                    onDidDismiss={() => setCommentMenuOpen(false)}
                    buttons={menuButtons}
                  />
                  <IonAlert
                    isOpen={blockConfirmOpen}
                    onDidDismiss={() => setBlockConfirmOpen(false)}
                    header="Bloccare questo utente?"
                    message="Non vedrai più i suoi post e commenti nella community. Potrai sbloccarlo dalle impostazioni."
                    buttons={[
                      { text: "Annulla", role: "cancel" },
                      {
                        text: "Blocca",
                        role: "destructive",
                        handler: () => {
                          const authorId = element?.created_by?.id;
                          if (!authorId) return;
                          dispatch(blockUser(authorId))
                            .unwrap()
                            .then(() => {
                              dispatch(hidePostsByUser(authorId));
                              present({
                                message: "Utente bloccato.",
                                duration: 2500,
                              });
                            })
                            .catch(() =>
                              present({
                                message:
                                  "Impossibile bloccare l'utente. Riprova.",
                                duration: 3000,
                                color: "danger",
                              }),
                            );
                        },
                      },
                    ]}
                  />
                  <IonAlert
                    isOpen={editOpen}
                    header="Modifica commento"
                    cssClass="ds-edit-alert"
                    onDidDismiss={() => setEditOpen(false)}
                    inputs={[
                      {
                        name: "content",
                        type: "textarea",
                        value: comment?.content ?? "",
                        placeholder: "Scrivi il tuo commento…",
                        // Ionic's default textarea is two or three lines tall, so
                        // editing a long comment meant scrolling a slot you could
                        // not read. `rows` sets the height; the CSS in
                        // PostFooter.css sizes the text to match the comment it is
                        // editing.
                        attributes: {
                          rows: 8,
                          maxlength: MAX_COMMENT_LENGTH,
                        },
                      },
                    ]}
                    buttons={[
                      { text: "Annulla", role: "cancel" },
                      {
                        text: "Salva",
                        handler: (data) => editComment(data?.content),
                      },
                    ]}
                  />
                  <IonAlert
                    isOpen={confirmDeleteOpen}
                    onDidDismiss={() => setConfirmDeleteOpen(false)}
                    header="Conferma"
                    message="Sei sicuro di voler eliminare questo commento?"
                    buttons={[
                      { text: "Annulla", role: "cancel" },
                      {
                        text: "Elimina",
                        role: "confirm",
                        handler: deleteComment,
                      },
                    ]}
                  />
                </>
              );
            })()}
          </div>
        </IonItem>
      )}
      {userId !== element?.created_by?.id && (
        <ReportModal
          type={postGroup ? "post" : "comment"}
          element_id={element_id}
          isOpen={reportIsOpen}
          setIsOpen={setReportIsOpen}
          setReported={setReported}
        />
      )}
      <IonModal
        isOpen={likesListOpen}
        onDidDismiss={() => setLikesListOpen(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Reazioni</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setLikesListOpen(false)}>
                Chiudi
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {topReactionTypes(
            reactionsFromStore,
            COMMENT_REACTIONS.length,
          ).length > 0 && (
            <div
              className="likes-list__summary"
              aria-label="Riepilogo reazioni"
            >
              {topReactionTypes(
                reactionsFromStore,
                COMMENT_REACTIONS.length,
              ).map((type) => (
                <span className="likes-list__summary-item" key={type}>
                  <span className="reaction-emoji">
                    {getReactionEmoji(type)}
                  </span>
                  <span>{Number(reactionsFromStore?.[type]) || 0}</span>
                </span>
              ))}
            </div>
          )}
          <IonList>
            {(likesList || []).length === 0 && !likesInheritedCount && (
              <IonItem>
                <IonText color="medium">Nessuna reazione</IonText>
              </IonItem>
            )}
            {(likesList || []).map((item, idx) => {
              if (typeof item === "string") {
                const label = item.trim();
                if (!label) return null;
                return (
                  <IonItem key={`like-str-${idx}-${label}`}>
                    <IonAvatar slot="start">
                      <ImageLoader imageType="user" image={null} />
                    </IonAvatar>
                    <IonText>{label}</IonText>
                  </IonItem>
                );
              }
              if (!item || typeof item !== "object") return null;

              const userForAvatar =
                item.created_by && typeof item.created_by === "object"
                  ? {
                      ...item.created_by,
                      storage_path:
                        item.created_by.storage_path || item.storage_path,
                    }
                  : item;

              const displayName =
                userForAvatar.subscriber_full_name ||
                item.subscriber_full_name ||
                item.full_name ||
                item.name ||
                "";
              if (!displayName) return null;

              const avatarSrc = resolveUserAvatarSrc(userForAvatar);
              const profileUser =
                userForAvatar?.id != null ? userForAvatar : item;
              const canNavigate = canOpenProfile(profileUser);
              const rowKey =
                profileUser?.id != null
                  ? `like-${profileUser.id}`
                  : `like-${idx}`;
              const aggregateReactionTypes = topReactionTypes(
                reactionsFromStore,
                COMMENT_REACTIONS.length,
              );
              const rowReactionType =
                getLikeItemReactionType(item) ||
                (aggregateReactionTypes.length === 1
                  ? aggregateReactionTypes[0]
                  : null);

              return (
                <IonItem
                  key={rowKey}
                  button={canNavigate}
                  detail={false}
                  onClick={() => {
                    if (canNavigate) {
                      goToProfile(profileUser);
                      setLikesListOpen(false);
                    }
                  }}
                  style={{ cursor: canNavigate ? "pointer" : "default" }}
                >
                  <IonAvatar slot="start">
                    <ImageLoader
                      key={avatarSrc || rowKey}
                      imageType="user"
                      image={avatarSrc}
                    />
                  </IonAvatar>
                  <IonText>{displayName}</IonText>
                  {rowReactionType && (
                    <span
                      slot="end"
                      className="likes-list__reaction"
                      aria-label="Reazione"
                    >
                      {getReactionEmoji(rowReactionType)}
                    </span>
                  )}
                </IonItem>
              );
            })}
            {likesInheritedCount > 0 && (
              <IonItem lines="none" className="likes-list__inherited">
                <IonText color="medium">
                  {likesInheritedCount === 1
                    ? "+ 1 altra reazione"
                    : `+ altre ${likesInheritedCount} reazioni`}
                </IonText>
              </IonItem>
            )}
          </IonList>
        </IonContent>
      </IonModal>
      <PermissionPopupModal
        isOpen={isLikeModalOpen}
        setIsOpen={setIsLikeModalOpen}
        message={likePopupText}
      />
      <PermissionPopupModal
        isOpen={isViewCommentsModalOpen}
        setIsOpen={setIsViewCommentsModalOpen}
        message={viewCommentsPopupText}
      />
    </>
  );
}
